import { CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import * as snackbarSelectors from './../../../store/snackbarsSlice/snackbarsSelectors';
import { useEffect } from 'react';
import { snackbarActions } from 'store/snackbarsSlice/snackbarsSlice';
import NotificationDismissButton from '../Buttons/NotificationDismissButton/NotificationDismissButton';
import styles from './Notifier.module.css';

function Notifier() {
    const dispatch = useDispatch();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const displayed = useSelector(snackbarSelectors.displayedSnackbarsSelector);
    const snackbars = useSelector(snackbarSelectors.snackbarsSelector);
    const toClose = useSelector(snackbarSelectors.toCloseSnackbarsSelector);
    useEffect(() => {
        snackbars.forEach(({ key, title, message, loading, closeIcon, options = {} }) => {
            // Do nothing if snackbar is already displayed
            if (displayed.includes(key)) return;

            // closeIcon: an X instead of the "Dismiss" text button (opt-in, e.g. the Slack result snackbars).
            const action = (key) => (
                <>
                    {options?.action}
                    {!loading && <NotificationDismissButton notificationId={key} icon={!!closeIcon} />}
                </>
            );

            // Display snackbar using notistack
            // Optional bold title above a secondary line (kept as plain strings so the store stays serializable).
            const text = title ? (
                <span className={styles.content}>
                    <span className={styles.title}>{title}</span>
                    {message && <span className={styles.message}>{message}</span>}
                </span>
            ) : (
                message
            );
            // `loading` shows a spinner in front, for a request still in progress (closed via snackbarActions.closeSnackbar).
            const content = loading ? (
                <span className={styles.loading}>
                    <CircularProgress size={20} />
                    {text}
                </span>
            ) : (
                text
            );

            enqueueSnackbar(content, {
                ...options,
                // notistack 2 applies a per-snackbar className to its content (a per-snackbar style is ignored).
                ...(loading && { className: [options.className, styles['loading-snackbar']].filter(Boolean).join(' ') }),
                action,
                onClose: (event, reason, key) => {
                    if (options.onClose) {
                        options.onClose(event, reason, key);
                    }
                    // Dispatch action to remove snackbar from redux store
                    dispatch(snackbarActions.removeSnackbar(key));
                },
                onExited: () => dispatch(snackbarActions.removeSnackbar(key)),
            });
            // Keep track of snackbars that we've displayed
            dispatch(snackbarActions.updateDisplayedSnackbars(key));

            return () => {
                snackbars.forEach((snackbar) => {
                    if (snackbar.dissmised) dispatch(snackbarActions.removeSnackbar(key));
                });
            };
        });
    }, [snackbars, dispatch, enqueueSnackbar, displayed]);

    // Runs after the effect above, so a snackbar enqueued and closed in the same update is still closed.
    useEffect(() => {
        toClose.forEach((key) => {
            closeSnackbar(key);
            dispatch(snackbarActions.snackbarClosed(key));
        });
    }, [toClose, closeSnackbar, dispatch]);
    return null;
}

export default Notifier;
